import { useEffect } from 'react';
import { StringUtils } from '@manifoldxyz/js-ts-utils';
import XMarkIcon from '@/assets/manifold-icons/x.svg';

interface SideColumnModalProps {
  isOpen: boolean;
  onClose: () => void;
  disableCloseButton?: boolean;
  needsDefinedHeightContainer?: boolean;
  hideCloseButton?: boolean;
  children: React.ReactNode;
}

export default function SideColumnModal({
  isOpen,
  onClose,
  disableCloseButton = false,
  needsDefinedHeightContainer = false,
  hideCloseButton = false,
  children,
}: SideColumnModalProps) {
  // Prevent body scroll when modal is open on mobile
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop - full screen on mobile, hidden on desktop */}
      <div
        className={StringUtils.classnames(
          'm-claim--fixed',
          'm-claim--inset-0',
          'm-claim--z-50',
          'm-claim--bg-neutral-900/80',
          'md:m-claim--hidden', // Hide backdrop on desktop
        )}
        onClick={onClose}
        role="presentation"
      />

      {/* Modal container */}
      <div
        className={StringUtils.classnames(
          'm-claim--fixed',
          'm-claim--inset-0',
          'm-claim--z-50',
          'm-claim--flex',
          'm-claim--items-center',
          'm-claim--justify-center',
          'md:m-claim--w-[424px]', // 400px column + 1.5rem left padding
          'md:m-claim--left-auto',
          'md:m-claim--right-auto',
          'md:m-claim--h-full', // Full height on desktop
        )}
      >
        {/* Modal content */}
        <div
          className={StringUtils.classnames(
            'm-claim--relative', // Changed from w-full to relative
            'm-claim--w-full',
            'm-claim--h-full',
            'm-claim--flex',
            'm-claim--flex-col',
            'm-claim--bg-surface-primary',
            'm-claim--p-6',
            'm-claim--overflow-hidden',
            'md:m-claim--rounded-l-md', // Round only left side on desktop
            'md:m-claim--h-full', // Full height on desktop
            'md:m-claim--max-h-full', // Remove max height constraint on desktop
            'md:m-claim--p-6', // Add extra padding on desktop to account for the main container padding
          )}
        >
          {/* Close button */}
          {!hideCloseButton && (
            <button
              onClick={onClose}
              className={StringUtils.classnames(
                'm-claim--absolute',
                'm-claim--right-6',
                'm-claim--top-6',
                'm-claim--p-2',
                'm-claim--text-secondary',
                'm-claim--transition-colors',
                'm-claim--z-[60]', // Added higher z-index
                'm-claim--bg-surface-secondary', // Added background color
                'm-claim--rounded-full', // Added full rounded border radius
                'hover:m-claim--bg-surface-accent',
                disableCloseButton && 'm-claim--opacity-50',
              )}
              style={{
                outline: '2px solid transparent',
                outlineOffset: '2px',
              }}
              aria-label="Close modal"
              disabled={disableCloseButton}
            >
              <img
                src={XMarkIcon}
                alt="Close"
                className="m-claim--size-6"
              />
            </button>
          )}

          {/* Modal content - Scrollable container with conditional padding for close button */}
          <div className="m-claim--flex-1 m-claim--overflow-y-auto">
            <div
              className={StringUtils.classnames({
                'm-claim--min-h-full': !needsDefinedHeightContainer,
                'm-claim--h-full': needsDefinedHeightContainer,
              })}
            >
              {children}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
